import React, { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { 
  X, ChevronRight, Clock, AlertCircle, CheckCircle2, TrendingUp, 
  TrendingDown, Minus, Flame, Brain, Target, RefreshCw, Activity, 
  Battery, Zap, User, AlertTriangle, Calendar, Send, ChevronDown,
  ChevronUp, Layers, BarChart3, Shield, Award, FileText, History,
  MessageSquare, Briefcase, Heart, ArrowUpRight, ArrowRight,
  Coffee, Plane, CalendarOff, UserCheck, Flag, Play, Pause
} from 'lucide-react';
import { toast } from 'sonner';
import { DESIGN } from '../DesignSystem';
import { Modal } from './FocusModals';

// ================================================
// UTILITY FUNCTIONS
// ================================================
export const getBurnoutConfig = (level) => {
  switch (level) {
    case 'low': return { color: '#10B981', bg: DESIGN.colors.successSubtle, label: 'Low Risk', icon: CheckCircle2 };
    case 'medium': return { color: DESIGN.colors.amber, bg: DESIGN.colors.amberSubtle, label: 'Medium Risk', icon: AlertCircle };
    case 'high': return { color: '#EF4444', bg: DESIGN.colors.errorSubtle, label: 'High Risk', icon: Flame };
    case 'critical': return { color: '#DC2626', bg: '#FEE2E2', label: 'Critical', icon: Flame };
    default: return { color: DESIGN.colors.textMuted, bg: DESIGN.colors.background, label: 'Unknown', icon: AlertCircle };
  }
};

export const getSeverityConfig = (severity) => {
  switch (severity) {
    case 'critical': return { color: DESIGN.colors.error, bg: DESIGN.colors.errorSubtle };
    case 'high': return { color: '#EA580C', bg: 'rgba(249, 115, 22, 0.1)' };
    case 'medium': return { color: '#D97706', bg: DESIGN.colors.amberSubtle };
    default: return { color: DESIGN.colors.success, bg: DESIGN.colors.successSubtle };
  }
};

// Get score color based on thresholds
const getScoreColor = (score) => {
  if (score >= 85) return '#DC2626';
  if (score >= 70) return DESIGN.colors.amber;
  return '#10B981';
};

// ================================================
// TREND SPARKLINE
// ================================================
export const TrendSparkline = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 60;
  const height = 24;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ================================================
// COGNITIVE LOAD HISTORY CHART
// ================================================
const CognitiveLoadChart = ({ data }) => {
  const chartData = data || [45, 52, 58, 72, 78, 85, 89, 94];
  const weeks = ['8w', '7w', '6w', '5w', '4w', '3w', '2w', '1w'];
  const max = 100;
  const height = 80;
  const width = 100;

  return (
    <div 
      className="p-4 rounded-xl"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
          Cognitive Load History
        </h5>
        <span className="text-[10px]" style={{ color: DESIGN.colors.textMuted }}>Last 8 weeks</span>
      </div>
      <div className="relative h-20">
        {/* Threshold lines */}
        <div 
          className="absolute w-full border-t border-dashed" 
          style={{ top: `${100 - 85}%`, borderColor: DESIGN.colors.error + '40' }}
        >
          <span className="absolute -top-2 right-0 text-[8px] font-bold" style={{ color: DESIGN.colors.error }}>85</span>
        </div>
        <div 
          className="absolute w-full border-t border-dashed" 
          style={{ top: `${100 - 70}%`, borderColor: DESIGN.colors.amber + '40' }}
        >
          <span className="absolute -top-2 right-0 text-[8px] font-bold" style={{ color: DESIGN.colors.amber }}>70</span>
        </div>
        
        {/* Chart */}
        <svg width="100%" height="100%" viewBox={`0 0 ${chartData.length * 40} ${height}`} preserveAspectRatio="none">
          {/* Area fill */}
          <defs>
            <linearGradient id="loadGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={DESIGN.colors.error} stopOpacity="0.3" />
              <stop offset="100%" stopColor={DESIGN.colors.error} stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path
            d={`M 0 ${height} ${chartData.map((val, i) => `L ${i * 40 + 20} ${height - (val / max) * height}`).join(' ')} L ${(chartData.length - 1) * 40 + 20} ${height} Z`}
            fill="url(#loadGradient)"
          />
          {/* Line */}
          <polyline
            points={chartData.map((val, i) => `${i * 40 + 20},${height - (val / max) * height}`).join(' ')}
            fill="none"
            stroke={DESIGN.colors.error}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Points */}
          {chartData.map((val, i) => (
            <circle
              key={i}
              cx={i * 40 + 20}
              cy={height - (val / max) * height}
              r="4"
              fill="white"
              stroke={getScoreColor(val)}
              strokeWidth="2"
            />
          ))}
        </svg>
      </div>
      <div className="flex justify-between mt-2">
        {weeks.map((w, i) => (
          <span key={i} className="text-[9px]" style={{ color: DESIGN.colors.textMuted }}>{w}</span>
        ))}
      </div>
    </div>
  );
};

// ================================================
// LANE ALIGNMENT CHART
// ================================================
const LaneAlignmentChart = ({ data, primaryLane }) => {
  const chartData = data || [85, 72, 45, 32, 18, 11, 15, 11];
  const weeks = ['8w', '7w', '6w', '5w', '4w', '3w', '2w', '1w'];

  return (
    <div 
      className="p-4 rounded-xl"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
          Lane Alignment History
        </h5>
        <div className="flex items-center gap-1.5">
          <div 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: '#FB923C' }}
          />
          <span className="text-[10px] font-medium" style={{ color: DESIGN.colors.textMuted }}>{primaryLane || 'Marketing'}</span>
        </div>
      </div>
      <div className="flex items-end gap-2 h-16">
        {chartData.map((val, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full rounded-t transition-all"
              style={{ 
                height: `${val}%`,
                backgroundColor: val >= 50 ? '#10B981' : val >= 30 ? DESIGN.colors.amber : DESIGN.colors.error,
                minHeight: '4px'
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2">
        {weeks.map((w, i) => (
          <span key={i} className="text-[9px] text-center flex-1" style={{ color: DESIGN.colors.textMuted }}>{w}</span>
        ))}
      </div>
      <p className="text-[10px] mt-3 text-center" style={{ color: DESIGN.colors.textMuted }}>
        % of work in primary cognitive lane
      </p>
    </div>
  );
};

// ================================================
// TIME OFF HISTORY CALENDAR
// ================================================
const TimeOffHistoryPanel = ({ expanded, onToggle }) => {
  const timeOffHistory = [
    { period: 'Jan 2-4, 2026', days: 3, daysAgo: 47, type: 'PTO' },
    { period: 'Dec 24-26, 2025', days: 3, daysAgo: 56, type: 'Holiday' },
    { period: 'Nov 28-29, 2025', days: 2, daysAgo: 82, type: 'Holiday' },
    { period: 'Oct 14-18, 2025', days: 5, daysAgo: 127, type: 'Vacation' },
    { period: 'Jul 4, 2025', days: 1, daysAgo: 229, type: 'Holiday' }
  ];

  const getRecencyColor = (daysAgo) => {
    if (daysAgo <= 30) return '#10B981';
    if (daysAgo <= 60) return DESIGN.colors.amber;
    return DESIGN.colors.error;
  };

  return (
    <div 
      className="rounded-xl overflow-hidden border"
      style={{ borderColor: DESIGN.colors.border }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
          <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
            Time Off History
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: DESIGN.colors.amberSubtle, color: DESIGN.colors.amber }}>
            Last 12 months
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-4 pt-0 space-y-2">
          {timeOffHistory.map((item, idx) => (
            <div 
              key={idx}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: 'white' }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-2 h-8 rounded-full"
                  style={{ backgroundColor: getRecencyColor(item.daysAgo) }}
                />
                <div>
                  <p className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>{item.period}</p>
                  <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{item.type}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>{item.days} days</p>
                <p className="text-xs" style={{ color: getRecencyColor(item.daysAgo) }}>{item.daysAgo}d ago</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ================================================
// BURNOUT ALERT CARD
// ================================================
export const BurnoutAlertCard = ({ member, onClick }) => {
  const burnout = member?.burnout_risk;
  if (!burnout) return null;
  
  const config = getBurnoutConfig(burnout.risk_level);
  const RiskIcon = config.icon;
  const TrendIcon = burnout.risk_trend === 'rising' ? TrendingUp : 
                    burnout.risk_trend === 'decreasing' ? TrendingDown : Minus;
  const trendColor = burnout.risk_trend === 'rising' ? DESIGN.colors.error : 
                     burnout.risk_trend === 'decreasing' ? '#10B981' : DESIGN.colors.textMuted;
  
  return (
    <div 
      className="rounded-2xl p-5 cursor-pointer transition-all hover:shadow-lg group"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `2px solid ${config.color}`,
        boxShadow: DESIGN.shadows.lg
      }}
      onClick={onClick}
      data-testid={`burnout-alert-card-${member.member_id}`}
    >
      <div className="flex items-start gap-4">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center text-base font-bold flex-shrink-0"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.primary}20 0%, ${DESIGN.colors.primary}10 100%)`,
            color: DESIGN.colors.textSecondary
          }}
        >
          {member.avatar || member.member_name?.slice(0, 2) || '??'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-base font-bold truncate" style={{ color: DESIGN.colors.textPrimary }}>
              {member.member_name}
            </h4>
            <span 
              className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 flex-shrink-0"
              style={{ backgroundColor: config.bg, color: config.color }}
            >
              <RiskIcon className="w-3.5 h-3.5" />
              {config.label}
            </span>
          </div>
          <p className="text-xs mb-3 truncate" style={{ color: DESIGN.colors.textMuted }}>{member.title}</p>
          
          {/* Score with Trend */}
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black" style={{ color: config.color }}>{burnout.risk_score}</span>
              <div className="flex flex-col">
                <span className="text-[10px] leading-tight" style={{ color: DESIGN.colors.textMuted }}>/100</span>
                <div className="flex items-center gap-1" style={{ color: trendColor }}>
                  <TrendIcon className="w-3 h-3" />
                  <span className="text-[10px] font-bold">
                    {burnout.score_delta > 0 ? '+' : ''}{burnout.score_delta?.toFixed(0) || 0}
                  </span>
                </div>
              </div>
            </div>
            {burnout.trend_data && burnout.trend_data.length > 0 && (
              <TrendSparkline data={burnout.trend_data} color={config.color} />
            )}
          </div>
          
          {/* Days to Critical Badge */}
          {burnout.days_to_critical !== null && burnout.days_to_critical !== undefined && (
            <div 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ 
                backgroundColor: burnout.days_to_critical === 0 ? DESIGN.colors.errorSubtle : DESIGN.colors.amberSubtle,
                color: burnout.days_to_critical === 0 ? DESIGN.colors.error : '#D97706'
              }}
            >
              <Clock className="w-3.5 h-3.5" />
              {burnout.days_to_critical === 0 ? 'Critical Now' : `${burnout.days_to_critical}d to critical`}
            </div>
          )}
          
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
            <div 
              className="h-full rounded-full transition-all"
              style={{ width: `${burnout.risk_score}%`, backgroundColor: config.color }}
            />
          </div>
          
          <div className="flex items-center gap-3 mt-3 text-xs" style={{ color: DESIGN.colors.textMuted }}>
            <span className="font-medium">{burnout.active_tasks} tasks</span>
            <span>•</span>
            <span className="font-medium">{burnout.lane_mismatch_percentage}% misaligned</span>
            {burnout.weeks_above_capacity > 0 && (
              <>
                <span>•</span>
                <span className="font-bold" style={{ color: DESIGN.colors.error }}>{burnout.weeks_above_capacity}w over</span>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: DESIGN.colors.textMuted }} />
      </div>
    </div>
  );
};

// ================================================
// PREMIUM RISK DRIVER CARD
// ================================================
const DRIVER_ICON_MAP = {
  ListTodo: Target,
  Compass: Target,
  RefreshCw: RefreshCw,
  Brain: Brain,
  Scale: Activity,
  Battery: Battery
};

const DRIVER_CONTEXT = {
  'Lane Misalignment': 'A Marketing executive doing non-Marketing work is operating in a cognitively foreign environment — the mental cost is significantly higher than aligned work.',
  'Cognitive Complexity': 'Consecutive L3/L4 tasks with no recovery work creates compounding mental fatigue that accumulates over time.',
  'Decision Burden': 'Simultaneous ownership decisions exceed healthy cognitive capacity for sustained periods and lead to decision fatigue.',
  'Context Switching': 'Frequent lane transitions create a fragmented cognitive state that prevents deep work and accelerates exhaustion.',
  'Workload Volume': 'Sustained high task volume without adequate recovery periods depletes cognitive reserves faster than they can be replenished.',
  'Time Pressure': 'Constant deadline pressure triggers stress responses that compound cognitive load beyond the actual work demands.'
};

export const RiskDriverBar = ({ driver, isPrimary }) => {
  const IconComponent = DRIVER_ICON_MAP[driver.icon] || AlertCircle;
  const severityConfig = getSeverityConfig(driver.severity);
  
  // Gradient color based on score
  const getBarGradient = (score) => {
    if (score >= 85) return 'linear-gradient(90deg, #F97316 0%, #DC2626 100%)';
    if (score >= 70) return 'linear-gradient(90deg, #FBBF24 0%, #F97316 100%)';
    if (score >= 50) return 'linear-gradient(90deg, #10B981 0%, #FBBF24 100%)';
    return `linear-gradient(90deg, #10B981 0%, #10B981 100%)`;
  };
  
  return (
    <div 
      className={`rounded-2xl transition-all hover:shadow-lg ${isPrimary ? 'ring-2' : ''}`}
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.md,
        ringColor: isPrimary ? DESIGN.colors.error : 'transparent'
      }}
    >
      {isPrimary && (
        <div 
          className="px-4 py-2 rounded-t-2xl flex items-center gap-2"
          style={{ backgroundColor: DESIGN.colors.errorSubtle }}
        >
          <Flag className="w-3.5 h-3.5" style={{ color: DESIGN.colors.error }} />
          <span className="text-xs font-bold" style={{ color: DESIGN.colors.error }}>PRIMARY DRIVER</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: severityConfig.bg }}
          >
            <IconComponent className="w-6 h-6" style={{ color: severityConfig.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold" style={{ color: DESIGN.colors.textPrimary }}>{driver.name}</span>
              <span 
                className="text-xl font-black"
                style={{ color: severityConfig.color }}
              >
                {driver.score.toFixed(0)}%
              </span>
            </div>
            <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
              <div 
                className="h-full rounded-full transition-all"
                style={{ width: `${driver.score}%`, background: getBarGradient(driver.score) }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: DESIGN.colors.textMuted }}>{driver.description}</p>
            
            {/* Context line */}
            <div 
              className="mt-3 p-3 rounded-lg border-l-2"
              style={{ 
                backgroundColor: DESIGN.colors.background,
                borderLeftColor: severityConfig.color
              }}
            >
              <p className="text-xs italic leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
                {DRIVER_CONTEXT[driver.name] || 'Contributing factor to cognitive load and burnout risk.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM ACTION CARD
// ================================================
const ActionCard = ({ action, onAction, loading, isPriority }) => {
  const iconMap = {
    'Recommend Time Off': Plane,
    'Adjust Lane Assignment': Layers,
    'Reduce Task Volume': Target,
    'Schedule Recovery Week': Calendar,
    'Cognitive Lane Realignment Session': MessageSquare,
    'Pause Non-Critical Decisions': Pause,
    'Flag for Executive Review': Flag,
    'Redistribute Tasks': RefreshCw,
    'Reduce Task Load': Target,
    'Realign to Primary Lane': Layers,
    'Schedule 1:1': MessageSquare
  };
  
  const Icon = iconMap[action.title] || Zap;
  
  return (
    <div 
      className={`rounded-2xl overflow-hidden transition-all hover:shadow-lg ${isPriority ? 'ring-2' : ''}`}
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.md,
        ringColor: isPriority ? DESIGN.colors.primary : 'transparent'
      }}
    >
      {isPriority && (
        <div 
          className="px-4 py-2 flex items-center gap-2"
          style={{ background: `linear-gradient(90deg, ${DESIGN.colors.primary}20 0%, ${DESIGN.colors.primary}05 100%)` }}
        >
          <Heart className="w-3.5 h-3.5" style={{ color: DESIGN.colors.primary }} />
          <span className="text-xs font-bold" style={{ color: DESIGN.colors.primary }}>RECOMMENDED ACTION</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: DESIGN.colors.primarySubtle }}
          >
            <Icon className="w-6 h-6" style={{ color: DESIGN.colors.primary }} />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold mb-1" style={{ color: DESIGN.colors.textPrimary }}>
              {action.title}
            </h4>
            <p className="text-xs mb-3" style={{ color: DESIGN.colors.textMuted }}>
              {action.description}
            </p>
            
            {action.impact && (
              <div 
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-3"
                style={{ backgroundColor: DESIGN.colors.successSubtle, color: '#10B981' }}
              >
                <TrendingDown className="w-3 h-3" />
                {action.impact}
              </div>
            )}
            
            {action.details && (
              <div 
                className="p-3 rounded-lg space-y-1.5 mb-3"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                {action.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{detail.label}</span>
                    <span 
                      className="text-xs font-bold"
                      style={{ color: detail.warning ? DESIGN.colors.amber : DESIGN.colors.textPrimary }}
                    >
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={() => onAction(action)}
          disabled={loading}
          className="w-full rounded-xl mt-2 font-semibold"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
            boxShadow: `0 0 15px ${DESIGN.colors.primary}30`
          }}
        >
          {loading ? 'Processing...' : 'Take Action'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// ================================================
// ELEVATED BURNOUT DETAIL PANEL
// ================================================
export const BurnoutDetailPanel = ({ member, onClose, onAction }) => {
  const [activeTab, setActiveTab] = useState('burnout');
  const [actionLoading, setActionLoading] = useState(null);
  const [timeOffExpanded, setTimeOffExpanded] = useState(false);
  const [messageComposerOpen, setMessageComposerOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  
  const burnout = member?.burnout_risk;
  const profile = member?.profile || {};
  
  // Enhanced action items - must be called before early return
  const enhancedActions = useMemo(() => {
    if (!member) return [];
    const baseActions = burnout?.action_items || [];
    const enhanced = [
      {
        id: 'time-off',
        title: 'Recommend Time Off',
        description: 'The most impactful intervention for cognitive recovery. Recommend strategic time off to allow full mental reset.',
        impact: 'Expected -15 to -25 pts after rest period',
        isPriority: true,
        details: [
          { label: 'Last time off', value: '47 days ago', warning: true },
          { label: 'Days taken this year', value: '6 of 15 days' },
          { label: 'Recommended duration', value: '3-5 consecutive days' }
        ]
      },
      ...baseActions.map(a => ({
        ...a,
        impact: 'Expected -8 to -12 pts over 2 weeks'
      })),
      {
        id: 'lane-realign',
        title: 'Adjust Lane Assignment',
        description: 'Realigning to primary cognitive lane will reduce the mental cost of work even if task volume stays the same.',
        impact: 'Expected -10 to -15 pts over 3 weeks',
        details: [
          { label: 'Current primary lane work', value: '11%', warning: true },
          { label: 'Target alignment', value: '60%+' },
          { label: 'Primary lane', value: 'Marketing' }
        ]
      },
      {
        id: 'recovery-week',
        title: 'Schedule Recovery Week',
        description: 'A structured lighter week with reduced meetings, no new task assignments, and protected deep work time.',
        impact: 'Expected -5 to -10 pts within week',
        details: [
          { label: 'Meeting reduction', value: '50%' },
          { label: 'New assignments', value: 'Paused' },
          { label: 'Focus time blocks', value: '4 hours/day' }
        ]
      },
      {
        id: 'realignment-session',
        title: 'Cognitive Lane Realignment Session',
        description: 'A structured one-on-one specifically designed to discuss lane misalignment and create a return-to-primary-domain plan.',
        impact: 'Foundation for long-term recovery'
      },
      {
        id: 'pause-decisions',
        title: 'Pause Non-Critical Decisions',
        description: 'Address Decision Burden by identifying which decisions could be delegated to team leads to reduce ownership load.',
        impact: 'Expected -5 to -8 pts immediately',
        details: [
          { label: 'Current decisions owned', value: '9 active' },
          { label: 'Delegation candidates', value: '4 identified' }
        ]
      },
      {
        id: 'executive-flag',
        title: 'Flag for Executive Review',
        description: 'For Critical cases, escalate the situation to the C-Suite with a full cognitive load report attached.',
        impact: 'Organizational visibility'
      }
    ];
    return enhanced;
  }, [burnout, member]);
  
  // Early return after all hooks
  if (!member) return null;
  
  const burnoutConfig = getBurnoutConfig(burnout?.risk_level);
  const BurnoutIcon = burnoutConfig.icon;
  const isHighRisk = burnout?.risk_level === 'high' || burnout?.risk_level === 'critical';
  
  const handleAction = async (action) => {
    if (action.id === 'time-off') {
      setMessageComposerOpen(true);
      setMessageText(`Hi ${member.member_name?.split(' ')[0]},\n\nI've been reviewing our team's workload and wanted to check in with you. The data shows you've been carrying a significant cognitive load recently, and I think some strategic time off could be really valuable for you.\n\nWould you be open to taking a few days off soon? I'd like to make sure you have the time and space to recharge properly. We can work together to ensure coverage during your time away.\n\nLet me know your thoughts.\n\nBest,`);
      return;
    }
    
    setActionLoading(action.id);
    try {
      await onAction(member, action);
      toast.success(`Action initiated: ${action.title}`);
    } catch (err) {
      toast.error('Failed to complete action');
    } finally {
      setActionLoading(null);
    }
  };

  const TrendIcon = burnout?.risk_trend === 'rising' ? TrendingUp : 
                    burnout?.risk_trend === 'decreasing' ? TrendingDown : Minus;
  const trendColor = burnout?.risk_trend === 'rising' ? DESIGN.colors.error : 
                     burnout?.risk_trend === 'decreasing' ? '#10B981' : DESIGN.colors.textMuted;

  // Enhanced profile data
  const enhancedProfile = {
    ...profile,
    department: profile.department || 'Marketing',
    email: profile.email || `${member.member_name?.toLowerCase().replace(' ', '.')}@company.com`,
    primaryLane: 'Marketing',
    primaryLaneColor: '#FB923C',
    cognitiveLevel: 'L3',
    startDate: 'Mar 15, 2023',
    employmentType: 'Full-time',
    secondaryLanes: ['Creative', 'Strategy'],
    workStyle: 'High-velocity execution with creative problem-solving',
    avgWeeklyTasks: 7.2,
    avgLoadScore: 68,
    peakLoadScore: 94,
    weeksAboveThreshold: 4,
    daysAvailable: 15,
    daysTaken: 6,
    daysRemaining: 9,
    lastTimeOff: '47 days ago',
    longestStretch: '47 days',
    onTimeDelivery: '88%',
    collaborationScore: '92%',
    decisionQuality: '85%',
    previousFlags: [
      { date: 'Nov 2025', score: 78, duration: '2 weeks', action: 'Task redistribution' },
      { date: 'Aug 2025', score: 72, duration: '1 week', action: 'Recovery time' }
    ]
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="burnout-detail-panel">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-2xl shadow-2xl overflow-y-auto animate-slide-in"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        {/* ELEVATED HEADER */}
        <div 
          className="sticky top-0 p-6 z-10"
          style={{ 
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
          }}
        >
          {/* Close button */}
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-white/80" />
          </button>
          
          {/* Person Info */}
          <div className="flex items-start gap-5 mb-6">
            {/* Avatar with pulsing ring for high risk */}
            <div className="relative">
              <div 
                className={`w-20 h-20 rounded-2xl flex items-center justify-center text-xl font-bold ${isHighRisk ? 'animate-pulse-ring' : ''}`}
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)', 
                  color: 'white',
                  border: `3px solid ${isHighRisk ? DESIGN.colors.error : 'rgba(255,255,255,0.2)'}`,
                  boxShadow: isHighRisk ? `0 0 20px ${DESIGN.colors.error}50` : 'none'
                }}
              >
                {member.avatar || member.member_name?.slice(0, 2)}
              </div>
              {isHighRisk && (
                <div 
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center animate-pulse"
                  style={{ backgroundColor: DESIGN.colors.error }}
                >
                  <AlertTriangle className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <h2 className="text-2xl font-black text-white mb-1">{member.member_name}</h2>
              <p className="text-sm text-slate-300 mb-2">{member.title}</p>
              {/* Cognitive Lane Pill */}
              <div 
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: `${enhancedProfile.primaryLaneColor}25`, color: enhancedProfile.primaryLaneColor }}
              >
                <Layers className="w-3 h-3" />
                {enhancedProfile.primaryLane} Lane
              </div>
            </div>
          </div>
          
          {/* RISK SCORE PANEL - Elevated */}
          <div 
            className="rounded-2xl p-5 relative overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.9) 100%)',
              border: `1px solid ${burnoutConfig.color}40`
            }}
          >
            {/* Background glow effect for high risk */}
            {isHighRisk && (
              <div 
                className="absolute inset-0 opacity-20"
                style={{ background: `radial-gradient(ellipse at top right, ${DESIGN.colors.error} 0%, transparent 60%)` }}
              />
            )}
            
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: burnoutConfig.bg }}
                >
                  <BurnoutIcon className="w-6 h-6" style={{ color: burnoutConfig.color }} />
                </div>
                <div>
                  <span className="text-xs text-slate-400 block mb-1">Risk Level</span>
                  <span className="text-lg font-bold text-white">{burnoutConfig.label}</span>
                  {/* Duration badge */}
                  <div className="mt-1">
                    <span 
                      className="text-xs font-semibold"
                      style={{ color: DESIGN.colors.amber }}
                    >
                      Elevated for 3 weeks
                    </span>
                  </div>
                </div>
              </div>
              
              {/* DRAMATIC SCORE */}
              <div className="text-right">
                <div className="flex items-baseline gap-1">
                  <span 
                    className="text-5xl font-black tracking-tight"
                    style={{ 
                      color: burnoutConfig.color,
                      textShadow: `0 0 30px ${burnoutConfig.color}50`
                    }}
                  >
                    {burnout?.risk_score}
                  </span>
                  <span className="text-sm text-slate-500">/100</span>
                </div>
                <div className="flex items-center justify-end gap-2 mt-1">
                  <div 
                    className="flex items-center gap-1 px-2 py-0.5 rounded"
                    style={{ backgroundColor: `${trendColor}20`, color: trendColor }}
                  >
                    <TrendIcon className="w-4 h-4" />
                    <span className="text-sm font-black">
                      {(burnout?.score_delta || 0) > 0 ? '+' : ''}{(burnout?.score_delta || 0).toFixed(0)} pts
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress bar with threshold markers */}
            <div className="mt-5 relative">
              <div className="h-3 rounded-full overflow-hidden relative" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                {/* Threshold markers */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5"
                  style={{ left: '70%', backgroundColor: DESIGN.colors.amber }}
                />
                <div 
                  className="absolute top-0 bottom-0 w-0.5"
                  style={{ left: '85%', backgroundColor: DESIGN.colors.error }}
                />
                {/* Fill */}
                <div 
                  className="h-full rounded-full transition-all relative"
                  style={{ 
                    width: `${burnout?.risk_score || 0}%`, 
                    backgroundColor: burnoutConfig.color,
                    boxShadow: `0 0 10px ${burnoutConfig.color}`
                  }}
                />
              </div>
              <div className="flex justify-between mt-1.5 text-[10px] text-slate-500">
                <span>Low</span>
                <span style={{ marginLeft: '55%' }}>Elevated (70)</span>
                <span>Critical (85)</span>
              </div>
            </div>
            
            {/* Projection timeline */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <div>
                <span className="text-xs text-slate-400 block">Projected Impact</span>
                <span 
                  className="text-xs font-semibold"
                  style={{ color: DESIGN.colors.amber }}
                >
                  Burnout projected in 8-12 days without intervention
                </span>
              </div>
              {burnout?.days_to_critical === 0 && (
                <button
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white flex items-center gap-2 animate-pulse-glow"
                  style={{ 
                    backgroundColor: DESIGN.colors.error,
                    boxShadow: `0 0 20px ${DESIGN.colors.error}60`
                  }}
                >
                  <AlertTriangle className="w-4 h-4" />
                  Critical Now
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* TABS */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList 
            className="px-6 pt-4 mb-0 border-b grid grid-cols-4 gap-2" 
            style={{ backgroundColor: DESIGN.colors.background, borderColor: DESIGN.colors.border }}
          >
            {[
              { value: 'burnout', icon: Flame, label: 'Analysis' },
              { value: 'drivers', icon: Activity, label: 'Drivers' },
              { value: 'actions', icon: Zap, label: 'Actions' },
              { value: 'profile', icon: User, label: 'Profile' }
            ].map(tab => (
              <TabsTrigger 
                key={tab.value}
                value={tab.value} 
                className="text-xs gap-1.5 rounded-xl py-2.5"
                data-testid={`burnout-tab-${tab.value}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ================================================ */}
          {/* ANALYSIS TAB - ELEVATED */}
          {/* ================================================ */}
          <TabsContent value="burnout" className="p-6 space-y-5 mt-0">
            {/* AI-Generated Insight Banner */}
            <div 
              className="p-5 rounded-2xl border-l-4 relative overflow-hidden"
              style={{ 
                backgroundColor: DESIGN.colors.errorSubtle,
                borderLeftColor: DESIGN.colors.error,
                boxShadow: `inset 0 0 30px ${DESIGN.colors.error}10`
              }}
            >
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5" style={{ color: DESIGN.colors.error }} />
                  <h4 className="text-sm font-black uppercase tracking-wide" style={{ color: DESIGN.colors.error }}>
                    Immediate Attention Required
                  </h4>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: DESIGN.colors.textPrimary }}>
                  <strong>{member.member_name?.split(' ')[0]}</strong> has been operating outside their primary Marketing lane for <strong>3 consecutive weeks</strong> while managing <strong>9 active tasks</strong> at L3-L4 complexity. Combined with <strong>zero time off in 47 days</strong>, this pattern indicates acute burnout risk requiring immediate intervention.
                </p>
              </div>
            </div>
            
            {/* Key Metrics - Enhanced */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { 
                  label: 'Active Tasks', 
                  value: burnout?.active_tasks || 0, 
                  color: DESIGN.colors.primary,
                  subtext: 'Recommended max: 5-6 for CMO level',
                  warning: (burnout?.active_tasks || 0) > 6
                },
                { 
                  label: 'Lane Misalignment', 
                  value: `${burnout?.lane_mismatch_percentage || 0}%`, 
                  color: DESIGN.colors.amber,
                  subtext: 'Target: under 20% misalignment',
                  warning: (burnout?.lane_mismatch_percentage || 0) > 20
                },
                { 
                  label: 'Weeks Above Threshold', 
                  value: burnout?.weeks_above_capacity || 3, 
                  color: DESIGN.colors.error,
                  subtext: 'Consecutive weeks above 70 load',
                  warning: true
                }
              ].map((metric, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-2xl text-center relative overflow-hidden"
                  style={{ 
                    backgroundColor: DESIGN.colors.card,
                    boxShadow: DESIGN.shadows.md,
                    border: metric.warning ? `2px solid ${metric.color}30` : 'none'
                  }}
                >
                  {metric.warning && (
                    <div 
                      className="absolute top-2 right-2 w-2 h-2 rounded-full animate-pulse"
                      style={{ backgroundColor: metric.color }}
                    />
                  )}
                  <p className="text-3xl font-black" style={{ color: metric.color }}>{metric.value}</p>
                  <p className="text-xs font-bold uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>
                    {metric.label}
                  </p>
                  <p className="text-[10px] mt-2" style={{ color: DESIGN.colors.textMuted }}>
                    {metric.subtext}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Cognitive Load History Chart */}
            <CognitiveLoadChart data={burnout?.trend_data} />
            
            {/* Lane Alignment History Chart */}
            <LaneAlignmentChart 
              data={[85, 72, 45, 32, 18, 11, 15, 11]} 
              primaryLane={enhancedProfile.primaryLane}
            />
          </TabsContent>

          {/* ================================================ */}
          {/* DRIVERS TAB - ELEVATED */}
          {/* ================================================ */}
          <TabsContent value="drivers" className="p-6 space-y-4 mt-0">
            {burnout?.risk_drivers?.length > 0 ? (
              <>
                {/* Primary Driver - Featured */}
                <RiskDriverBar 
                  driver={burnout.risk_drivers[0]} 
                  isPrimary={true} 
                />
                
                {/* Other Drivers */}
                <div className="pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: DESIGN.colors.textMuted }}>
                    Contributing Factors
                  </h4>
                  <div className="space-y-3">
                    {burnout.risk_drivers.slice(1).map((driver, idx) => (
                      <RiskDriverBar key={idx} driver={driver} isPrimary={false} />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-center py-8" style={{ color: DESIGN.colors.textMuted }}>
                No risk drivers available
              </p>
            )}
          </TabsContent>

          {/* ================================================ */}
          {/* ACTIONS TAB - MAJOR EXPANSION */}
          {/* ================================================ */}
          <TabsContent value="actions" className="p-6 space-y-4 mt-0">
            {enhancedActions.map((action, idx) => (
              <ActionCard
                key={action.id || idx}
                action={action}
                onAction={handleAction}
                loading={actionLoading === action.id}
                isPriority={action.isPriority}
              />
            ))}
            
            {/* Time Off History Panel */}
            <TimeOffHistoryPanel 
              expanded={timeOffExpanded} 
              onToggle={() => setTimeOffExpanded(!timeOffExpanded)} 
            />
          </TabsContent>

          {/* ================================================ */}
          {/* PROFILE TAB - MAJOR EXPANSION */}
          {/* ================================================ */}
          <TabsContent value="profile" className="p-6 space-y-5 mt-0">
            {/* Personal Information */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
            >
              <div 
                className="px-5 py-3 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <User className="w-4 h-4" />
                  Personal Information
                </h4>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4">
                {[
                  { label: 'Name', value: member.member_name },
                  { label: 'Role', value: member.title },
                  { label: 'Email', value: enhancedProfile.email },
                  { label: 'Department', value: enhancedProfile.department },
                  { label: 'Start Date', value: enhancedProfile.startDate },
                  { label: 'Employment Type', value: enhancedProfile.employmentType }
                ].map((item, idx) => (
                  <div key={idx}>
                    <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: DESIGN.colors.textMuted }}>{item.label}</p>
                    <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{item.value}</p>
                  </div>
                ))}
                <div className="col-span-2 pt-2 border-t" style={{ borderColor: DESIGN.colors.border }}>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: DESIGN.colors.textMuted }}>Primary Lane</p>
                      <div 
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
                        style={{ backgroundColor: `${enhancedProfile.primaryLaneColor}15`, color: enhancedProfile.primaryLaneColor }}
                      >
                        <Layers className="w-3 h-3" />
                        {enhancedProfile.primaryLane}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: DESIGN.colors.textMuted }}>Cognitive Level</p>
                      <span 
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold"
                        style={{ backgroundColor: DESIGN.colors.primarySubtle, color: DESIGN.colors.primary }}
                      >
                        {enhancedProfile.cognitiveLevel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Cognitive Profile */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
            >
              <div 
                className="px-5 py-3 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <Brain className="w-4 h-4" />
                  Cognitive Profile
                </h4>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>Secondary Lanes</p>
                  <div className="flex gap-2">
                    {enhancedProfile.secondaryLanes.map((lane, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textSecondary }}
                      >
                        {lane}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: DESIGN.colors.textMuted }}>Working Style</p>
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{enhancedProfile.workStyle}</p>
                </div>
              </div>
            </div>
            
            {/* Workload History */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
            >
              <div 
                className="px-5 py-3 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <BarChart3 className="w-4 h-4" />
                  Workload History (90 Days)
                </h4>
              </div>
              <div className="p-5 grid grid-cols-4 gap-4">
                {[
                  { label: 'Avg Weekly Tasks', value: enhancedProfile.avgWeeklyTasks },
                  { label: 'Avg Load Score', value: enhancedProfile.avgLoadScore },
                  { label: 'Peak Load Score', value: enhancedProfile.peakLoadScore, warning: true },
                  { label: 'Weeks Over Threshold', value: enhancedProfile.weeksAboveThreshold, warning: true }
                ].map((item, idx) => (
                  <div key={idx} className="text-center">
                    <p 
                      className="text-2xl font-black"
                      style={{ color: item.warning ? DESIGN.colors.error : DESIGN.colors.textPrimary }}
                    >
                      {item.value}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Time Off Summary - With Warning */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ 
                backgroundColor: DESIGN.colors.card, 
                boxShadow: DESIGN.shadows.md,
                border: `2px solid ${DESIGN.colors.amber}30`
              }}
            >
              <div 
                className="px-5 py-3 border-b flex items-center justify-between"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <Calendar className="w-4 h-4" />
                  Time Off Summary
                </h4>
                <div 
                  className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: DESIGN.colors.amberSubtle, color: DESIGN.colors.amber }}
                >
                  <AlertCircle className="w-3 h-3" />
                  Recovery Overdue
                </div>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {[
                    { label: 'Days Available', value: enhancedProfile.daysAvailable },
                    { label: 'Days Taken', value: enhancedProfile.daysTaken },
                    { label: 'Days Remaining', value: enhancedProfile.daysRemaining }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <p className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{item.value}</p>
                      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>{item.label}</p>
                    </div>
                  ))}
                </div>
                <div 
                  className="p-4 rounded-xl flex items-center justify-between"
                  style={{ backgroundColor: DESIGN.colors.amberSubtle }}
                >
                  <div>
                    <p className="text-sm font-bold" style={{ color: DESIGN.colors.amber }}>
                      No rest period in {enhancedProfile.lastTimeOff}
                    </p>
                    <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                      Longest stretch without time off: {enhancedProfile.longestStretch}
                    </p>
                  </div>
                  <Coffee className="w-6 h-6" style={{ color: DESIGN.colors.amber }} />
                </div>
              </div>
            </div>
            
            {/* Performance Context */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
            >
              <div 
                className="px-5 py-3 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <Award className="w-4 h-4" />
                  Performance Context
                </h4>
              </div>
              <div className="p-5">
                <p className="text-xs mb-4" style={{ color: DESIGN.colors.textMuted }}>
                  High performance despite elevated burnout risk indicates a dedicated team member being pushed past sustainable limits.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'On-Time Delivery', value: enhancedProfile.onTimeDelivery, color: '#10B981' },
                    { label: 'Collaboration', value: enhancedProfile.collaborationScore, color: DESIGN.colors.primary },
                    { label: 'Decision Quality', value: enhancedProfile.decisionQuality, color: DESIGN.colors.blue }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <p className="text-2xl font-black" style={{ color: item.color }}>{item.value}</p>
                      <p className="text-[10px] uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Previous Risk Flags */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
            >
              <div 
                className="px-5 py-3 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h4 className="text-xs font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: DESIGN.colors.textMuted }}>
                  <History className="w-4 h-4" />
                  Previous Risk Flags
                </h4>
              </div>
              <div className="p-5 space-y-3">
                {enhancedProfile.previousFlags.map((flag, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: DESIGN.colors.background }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: DESIGN.colors.amberSubtle }}
                      >
                        <Flag className="w-5 h-5" style={{ color: DESIGN.colors.amber }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{flag.date}</p>
                        <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Duration: {flag.duration}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold" style={{ color: DESIGN.colors.amber }}>{flag.score}</p>
                      <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{flag.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Message Composer Modal */}
        {messageComposerOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMessageComposerOpen(false)} />
            <div 
              className="relative w-full max-w-lg rounded-2xl overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.xl }}
            >
              <div 
                className="p-5 border-b"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                  Recommend Time Off
                </h3>
                <p className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>
                  Send a supportive message recommending rest
                </p>
              </div>
              <div className="p-5">
                <Textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={12}
                  className="w-full rounded-xl text-sm"
                  style={{ 
                    backgroundColor: DESIGN.colors.background,
                    border: `1px solid ${DESIGN.colors.border}`,
                    color: DESIGN.colors.textPrimary
                  }}
                />
              </div>
              <div 
                className="p-5 border-t flex gap-3"
                style={{ borderColor: DESIGN.colors.border }}
              >
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setMessageComposerOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 rounded-xl font-bold"
                  style={{ 
                    background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                    boxShadow: DESIGN.shadows.glowPrimary
                  }}
                  onClick={() => {
                    toast.success('Message sent to ' + member.member_name);
                    setMessageComposerOpen(false);
                  }}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-pulse-ring {
          animation: pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulseRing {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
        }
        .animate-pulse-glow {
          animation: pulseGlow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 15px rgba(220, 38, 38, 0.6); }
          50% { box-shadow: 0 0 25px rgba(220, 38, 38, 0.9); }
        }
      `}</style>
    </div>
  );
};

// ================================================
// BURNOUT RISK SECTION
// ================================================
export const BurnoutRiskSection = ({ alerts, loading, onSelectMember, onAction }) => {
  if (loading) {
    return (
      <div 
        className="rounded-2xl p-6"
        style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
      >
        <div className="flex items-center gap-3 mb-5">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: DESIGN.colors.errorSubtle }}
          >
            <AlertTriangle className="w-5 h-5" style={{ color: DESIGN.colors.error }} />
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>Burnout Alerts</h3>
            <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Loading...</p>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: DESIGN.colors.background }} />
          ))}
        </div>
      </div>
    );
  }

  if (!alerts || alerts.length === 0) {
    return null;
  }

  return (
    <div 
      className="rounded-2xl p-6 transition-all hover:shadow-lg"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
      data-testid="burnout-risk-section"
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: DESIGN.colors.errorSubtle }}
        >
          <AlertTriangle className="w-5 h-5" style={{ color: DESIGN.colors.error }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>Burnout Alerts</h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{alerts.length} team member(s) at risk</p>
        </div>
      </div>
      <div className="space-y-4">
        {alerts.slice(0, 3).map((member) => (
          <BurnoutAlertCard 
            key={member.member_id} 
            member={member} 
            onClick={() => onSelectMember(member)}
          />
        ))}
      </div>
    </div>
  );
};

// ================================================
// ORG BURNOUT HEATMAP
// ================================================
export const OrgBurnoutHeatMap = ({ heatmap, loading }) => {
  if (loading || !heatmap || heatmap.length === 0) {
    return null;
  }

  return (
    <div 
      className="rounded-2xl p-6 transition-all hover:shadow-lg"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
      data-testid="org-burnout-heatmap"
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: DESIGN.colors.amberSubtle }}
        >
          <Activity className="w-5 h-5" style={{ color: DESIGN.colors.amber }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>Team Load Map</h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Department overview</p>
        </div>
      </div>
      <div className="space-y-3">
        {heatmap.map((dept, idx) => (
          <div 
            key={idx}
            className="p-4 rounded-xl"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                {dept.department}
              </span>
              <span 
                className="text-sm font-black"
                style={{ 
                  color: dept.avg_risk > 70 ? DESIGN.colors.error 
                    : dept.avg_risk > 50 ? DESIGN.colors.amber 
                    : DESIGN.colors.success 
                }}
              >
                {dept.avg_risk}%
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${dept.avg_risk}%`,
                  backgroundColor: dept.avg_risk > 70 ? DESIGN.colors.error 
                    : dept.avg_risk > 50 ? DESIGN.colors.amber 
                    : DESIGN.colors.success
                }}
              />
            </div>
            <p className="text-xs mt-2" style={{ color: DESIGN.colors.textMuted }}>
              {dept.member_count} members • {dept.at_risk_count} at risk
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default { 
  getBurnoutConfig, getSeverityConfig, TrendSparkline, BurnoutAlertCard, 
  RiskDriverBar, BurnoutDetailPanel, BurnoutRiskSection, OrgBurnoutHeatMap 
};
